import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BankAccountTransferRequest } from './types';
import { useClientData } from '../../../providers/ClientData.provider';
import { useOpenModule } from '../../../providers/OpenModule.provider';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import { BankAccountTransferDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import BankAccountTransferDialogueContext, { BankAccountTransferCtx } from './Dialogue/BankAccountTransferContext';
import BankAccountTransferDialogue from './Dialogue/BankAccountTransferDialogue';
import { usePendingData } from '../../../providers/PendingData.provider';

import usePostEftFund from '../../../hooks/useApi/usePostEftFund';
import usePostEftWithdraw from '../../../hooks/useApi/usePostEftWithdraw';

const { Box, SubTitle, Table, TextField, formattedCurrency } = vp;

type TablePropsHandlers = {
  fromTransactionDetails: () => vp.TableProps;
  fromBank: () => vp.TableProps;
  toTransactionDetails: () => vp.TableProps;
  toBank: () => vp.TableProps;
};

type BankAccountTransferRequestProps = {
  submitTransaction: boolean;
  defaultValues: BankAccountTransferRequest;
  sourceBank: vp.ClientBankAccountsLists;
  destBank: vp.ClientBankAccountsLists;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
};

type FC = FunctionComponent<BankAccountTransferRequestProps>;

const BankAccountTransferReview: FC = ({ defaultValues, submitTransaction, sourceBank, destBank }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.BankAccountTransfer.Review' });
  const history = useHistory();
  const { response } = useClientData();
  const { setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const [eftFundResponse, postEftFund] = usePostEftFund({});
  const [eftWithdrawResponse, postEftWithdraw] = usePostEftWithdraw({});

  const [shouldTransferBankAccount, setShouldTransferBankAccount] = useState(false);
  const [shouldFundEft, setShouldFundEft] = useState(false);
  const [showBankAccountTransferErrorAlert, setShowBankAccountTransferErrorAlert] = useState(false);
  const [bankAccountTransferError, setBankAccountTransferError] = useState('');
  const { reset } = usePendingData();
  const BankAccountTransfer: BankAccountTransferCtx = {
    showBankAccountTransferErrorAlert,
    bankAccountTransferError,
  };

  const handlers = {
    handleBankAccountTransferDialogue: (handlerProps: DialogueHandlerProps<BankAccountTransferDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: BankAccountTransferDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowBankAccountTransferErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onAccountTransfer: () => {
      setShouldFundEft(true);
      const nameParts = sourceBank.AccountHolderName.trim().split(' ');
      const isCompanyName = nameParts.length > 2 || nameParts.length < 2;

      postEftFund({
        Amount: Number(defaultValues.Amount.replace(/,/g, '') || 0),
        PortalUser: response.AccountID || '',
        FirstName: !isCompanyName ? sourceBank.AccountHolderName.split(' ')[0] || '' : '',
        LastName: !isCompanyName ? sourceBank.AccountHolderName.split(' ')[1] || '' : '',
        CompanyName: isCompanyName ? sourceBank.AccountHolderName : '',
        PhoneNumber: vp.NumbersOnly(sourceBank.Phone || ''),
        Address1: sourceBank.Address || '',
        City: sourceBank.City || '',
        Province: sourceBank ? sourceBank['Province/State'] || '' : '',
        Country: response.Country,
        PostalCode: sourceBank ? sourceBank['PostalCode/ZipCode'] || '' : '',
        Token: sourceBank.Token || '',
        FinancialInstitutionNumber: sourceBank.FinancialInstitutionNumber || '',
        BranchTransitNumber: sourceBank.BranchTransitNumber || '',
        AccountNumber: sourceBank.AccountNumber || '',
        Currency: defaultValues.Currency,
        ClientReferenceNumber: sourceBank.ClientReferenceNumber || '',
        institution: sourceBank.InstitutionName || '',
      });
    },
  };
  useEffect(() => {
    if (submitTransaction) {
      handlers.onAccountTransfer();
    }
  }, [submitTransaction]);
  useEffect(() => {
    if (shouldFundEft && eftFundResponse.Success) {
      setShouldTransferBankAccount(true);
      const nameParts = destBank.AccountHolderName.trim().split(' ');
      const isCompanyName = nameParts.length > 2 || nameParts.length < 2;

      postEftWithdraw({
        Amount: Number(defaultValues.Amount.replace(/,/g, '') || 0),
        PortalUser: response.AccountID || '',
        FirstName: !isCompanyName ? destBank.AccountHolderName.split(' ')[0] || '' : '',
        LastName: !isCompanyName ? destBank.AccountHolderName.split(' ')[1] || '' : '',
        CompanyName: isCompanyName ? destBank.AccountHolderName : '',
        PhoneNumber: vp.NumbersOnly(destBank.Phone || ''),
        Address1: destBank.Address || '',
        City: destBank.City || '',
        Province: destBank['Province/State'],
        Country: response.Country || '',
        PostalCode: destBank['PostalCode/ZipCode'],
        FinancialInstitutionNumber: destBank.FinancialInstitutionNumber || '',
        BranchTransitNumber: destBank.BranchTransitNumber || '',
        AccountNumber: destBank.AccountNumber || '',
        Currency: defaultValues.Currency || '',
        Token: destBank.Token || '',
        ClientReferenceNumber: destBank.ClientReferenceNumber || '',
        FlinksAccountID: destBank.FlinksAccountID || '',
        FlinksLoginID: destBank.FlinksLoginID || '',
        KYCPerformed: false,
        DelayBankingInfo: false,
        ParentTransactionID: eftFundResponse.TransactionID,
      });
      reset();
    }
  }, [eftFundResponse.Success, shouldFundEft]);

  useEffect(() => {
    if (shouldFundEft) {
      const { Success, ErrorMessage } = eftFundResponse;

      if (Success === false) {
        setShowBankAccountTransferErrorAlert(true);
        setBankAccountTransferError(ErrorMessage);
      }
      setShouldFundEft(false);
    }

    if (shouldTransferBankAccount) {
      const { Success, ErrorMessage } = eftWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTWithdrawal/${eftWithdrawResponse.TransactionID}/${defaultValues.Currency}/`,
          state: {
            transactionId: eftWithdrawResponse.TransactionID,
            activeModule: 'bankAccountTransfer',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowBankAccountTransferErrorAlert(true);
        setBankAccountTransferError(ErrorMessage);
      }
      setShouldTransferBankAccount(false);
    }
  }, [eftWithdrawResponse, eftFundResponse]);

  return (
    <Formik initialValues={defaultValues} onSubmit={handlers.onAccountTransfer} validateOnChange validateOnBlur>
      {({ handleSubmit }) => (
        <BankAccountTransferDialogueContext.Provider value={BankAccountTransfer}>
          <form onSubmit={handleSubmit}>
            <Box flx="col">
              <Box flx="col" width="50%" mt="md">
                <SubTitle subTitle={t('fromTransactionDetails', { defaultValue: 'From Transaction Details' })} />
                <Box mb="md">
                  <Table {...getTableProps('fromTransactionDetails')} />
                </Box>
                <SubTitle subTitle={t('fromBank', { defaultValue: 'From Bank' })} />
                <Box mb="lg">
                  <Table {...getTableProps('fromBank')} />
                </Box>
                <SubTitle subTitle={t('toTransactionDetails', { defaultValue: 'To Transaction Details' })} />
                <Box mb="md">
                  <Table {...getTableProps('toTransactionDetails')} />
                </Box>
                <SubTitle subTitle={t('toBank', { defaultValue: 'To Bank' })} />
                <Box mb="lg">
                  <Table {...getTableProps('toBank')} />
                </Box>
              </Box>
            </Box>
          </form>
          <BankAccountTransferDialogue handleDialogue={handlers.handleBankAccountTransferDialogue} />
        </BankAccountTransferDialogueContext.Provider>
      )}
    </Formik>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlersTable: TablePropsHandlers = {
      fromTransactionDetails: () => {
        const fromTransactionDetailsPair: KeyValuePair[] = [
          {
            display: t('currency', { defaultValue: 'Currency' }),
            value: (
              <TextField
                name="currency-confirmation"
                variant="background"
                placeholder={defaultValues.Currency || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('amount', { defaultValue: 'Amount' }),
            value: (
              <TextField
                name="amount-confirmation"
                variant="background"
                placeholder={`$${formattedCurrency(String(defaultValues.Amount))}` || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('country', { defaultValue: 'Country' }),
            value: (
              <TextField
                name="country-confirmation"
                variant="background"
                placeholder={response.Country || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const fromTransactionDetails: vp.TableProps = {
          name: 'from-transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('fromTransactionDetailsHeader', { defaultValue: 'From Transaction Details' }) },
            detailValue: { header: '' },
          },
          rows:
            fromTransactionDetailsPair.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })) || [],
        };

        return fromTransactionDetails;
      },
      fromBank: () => {
        const fromBankPair: KeyValuePair[] = [
          {
            display: t('currency', { defaultValue: 'Currency' }),
            value: (
              <TextField
                name="curency-confirmation"
                variant="background"
                placeholder={defaultValues.Currency || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('country', { defaultValue: 'Country' }),
            value: (
              <TextField
                name="country-confirmation"
                variant="background"
                placeholder={response.Country || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('institution', { defaultValue: 'Institution' }),
            value: (
              <Box ai="center">
                {sourceBank.BankID !== null && vp.hasBankImage(sourceBank.BankID) && (
                  <img
                    alt="bank logo"
                    src={`https://embed-dev.vopay.com/images/logos-banks/${sourceBank.BankID}.svg`}
                  />
                )}
                <span>{sourceBank.InstitutionName}</span>
              </Box>
            ),
          },
          {
            display: t('institutionNumber', { defaultValue: 'Institution Number' }),
            value: (
              <TextField
                name="financial-institution-confirmation"
                variant="background"
                placeholder={sourceBank.FinancialInstitutionNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('transitNumber', { defaultValue: 'Transit Number' }),
            value: (
              <TextField
                name="branch-transit-confirmation"
                variant="background"
                placeholder={sourceBank.BranchTransitNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('accountNumber', { defaultValue: 'Account Number' }),
            value: (
              <TextField
                name="account-number-confirmation"
                variant="background"
                placeholder={sourceBank.AccountNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const fromBankDetails: vp.TableProps = {
          name: 'from-bank-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('fromBankDetailsHeader', { defaultValue: 'From Bank Details' }) },
            detailValue: { header: '' },
          },
          rows: fromBankPair.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return fromBankDetails;
      },
      toTransactionDetails: () => {
        const toTransactionDetailsPair: KeyValuePair[] = [
          {
            display: t('currency', { defaultValue: 'Currency' }),
            value: (
              <TextField
                name="currency-confirmation"
                variant="background"
                placeholder={defaultValues.Currency || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('amount', { defaultValue: 'Amount' }),
            value: (
              <TextField
                name="amount-confirmation"
                variant="background"
                placeholder={`$${formattedCurrency(String(defaultValues.Amount))}` || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('country', { defaultValue: 'Country' }),
            value: (
              <TextField
                name="country-confirmation"
                variant="background"
                placeholder={response.Country || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const toTransactionDetails: vp.TableProps = {
          name: 'to-transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('toTransactionDetailsHeader', { defaultValue: 'To Transaction Details' }) },
            detailValue: { header: '' },
          },
          rows:
            toTransactionDetailsPair.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })) || [],
        };

        return toTransactionDetails;
      },
      toBank: () => {
        const toBankPair: KeyValuePair[] = [
          {
            display: t('currency', { defaultValue: 'Currency' }),
            value: (
              <TextField
                name="curency-confirmation"
                variant="background"
                placeholder={defaultValues.Currency || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('country', { defaultValue: 'Country' }),
            value: (
              <TextField
                name="country-confirmation"
                variant="background"
                placeholder={response.Country || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('institution', { defaultValue: 'Institution' }),
            value: (
              <Box ai="center">
                {destBank.BankID !== null && vp.hasBankImage(destBank.BankID) && (
                  <img alt="bank logo" src={`https://embed-dev.vopay.com/images/logos-banks/${destBank.BankID}.svg`} />
                )}
                <span>{destBank.InstitutionName}</span>
              </Box>
            ),
          },
          {
            display: t('institutionNumber', { defaultValue: 'Institution Number' }),
            value: (
              <TextField
                name="financial-institution-confirmation"
                variant="background"
                placeholder={destBank.FinancialInstitutionNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('transitNumber', { defaultValue: 'Transit Number' }),
            value: (
              <TextField
                name="branch-transit-confirmation"
                variant="background"
                placeholder={destBank.BranchTransitNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('accountNumber', { defaultValue: 'Account Number' }),
            value: (
              <TextField
                name="account-number-confirmation"
                variant="background"
                placeholder={destBank.AccountNumber || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const toBankDetails: vp.TableProps = {
          name: 'to-bank-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('toBankDetailsHeader', { defaultValue: 'To Bank Details' }) },
            detailValue: { header: '' },
          },
          rows: toBankPair.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return toBankDetails;
      },
    };

    return handlersTable[handler]();
  }
};

export default BankAccountTransferReview;
